import { useReactiveVar } from '@apollo/client';
import { Separator, Stack, Text } from '@fluentui/react';
import { setApprovalLayoutType } from 'approvals/MyApprovals';
import { ApprovalPanelTypes } from 'approvals/types';
import { ApprovalHistoryItemsOrderBy } from 'common/types/globalTypes';
import { ApprovalHistory_Query } from '../__generated__/ApprovalHistory';
import { AdditionalInformation } from '../AdditionalInformation';
import { BatchTransactionLayout } from '../BatchTransactionLayout';
import { InvoiceHeader } from '../Header';
import { useStyles } from '../index.styles';
import { InvoiceDetailedData } from '../InvoiceDetailedData';
import { InvoiceAccountingList } from '../InvoiceList';
import { InvoicePaymentsList } from '../InvoiceListPayCycle';
import { MenuActions } from '../MenuActions';
import { POItems } from '../POItems';
import { SignatureView } from '../Signature';
import { TripList } from '../Trips/TripList';
import { getApprovalOrderBy, isLayoutPaycycle } from '../utils';

export interface ApprovalHistoryDetailComponentProps {
  approvalHistoryDetails: ApprovalHistory_Query | undefined;
  onRefetchApprovalHistory: () => void;
  isApprovalFlow?: boolean;
}
export const ApprovalHistoryDetailComponent: React.FC<
  ApprovalHistoryDetailComponentProps
> = ({
  approvalHistoryDetails,
  onRefetchApprovalHistory,
  isApprovalFlow = false,
}) => {
  const styles = useStyles();

  const approvalLayoutType = useReactiveVar(setApprovalLayoutType);

  const approvalOrderBy = getApprovalOrderBy(approvalLayoutType);

  const isMiscellaneousApproval =
    approvalHistoryDetails?.approvalHistory?.layoutType ===
      'MISCELLANEOUS_APPROVAL' &&
    approvalHistoryDetails?.approvalHistory
      ?.approvalHistoryItemsByDisplayEntityApprovalId?.nodes.length > 0;

  const isBatchTransaction =
    approvalHistoryDetails?.approvalHistory?.layoutType ===
    'BATCH_TRANSACTION_APPROVAL';

  const { layoutType } = { ...approvalHistoryDetails?.approvalHistory };

  return (
    <>
      <InvoiceHeader data={approvalHistoryDetails?.approvalHistory} />
      <Separator />
      {approvalHistoryDetails?.approvalHistory && (
        <MenuActions
          data={approvalHistoryDetails?.approvalHistory}
          approvalPanelType={ApprovalPanelTypes.APPROVAL_HISTORY}
          onRefetch={() => onRefetchApprovalHistory?.()}
          blockAutoOpenFirstFile={isApprovalFlow}
        />
      )}
      <Separator />
      <InvoiceDetailedData
        data={approvalHistoryDetails?.approvalHistory}
        approvalOrderBy={approvalOrderBy as ApprovalHistoryItemsOrderBy[]}
        isHistory={true}
        isApprovalFlow={isApprovalFlow}
      />
      <Separator />
      {isMiscellaneousApproval && (
        <>
          <AdditionalInformation
            data={approvalHistoryDetails?.approvalHistory}
          />
          <Separator />
        </>
      )}
      {isBatchTransaction && (
        <BatchTransactionLayout
          data={
            approvalHistoryDetails?.approvalHistory
              ?.approvalHistoryItemsByDisplayEntityApprovalId!
          }
        />
      )}
      {approvalHistoryDetails?.approvalHistory
        ?.approvalHistoryItemsByDisplayEntityApprovalId?.nodes &&
        approvalHistoryDetails.approvalHistory
          .approvalHistoryItemsByDisplayEntityApprovalId.nodes.length > 0 &&
        !isMiscellaneousApproval &&
        !isBatchTransaction && (
          <Stack tokens={{ padding: '0px 0px 0px 10px' }}>
            {isLayoutPaycycle(
              approvalHistoryDetails?.approvalHistory.layoutType!
            ) ? (
              <InvoicePaymentsList
                data={
                  approvalHistoryDetails?.approvalHistory
                    .approvalHistoryItemsByDisplayEntityApprovalId
                }
                isPaymentLink={
                  approvalHistoryDetails?.approvalHistory.layoutType ===
                  'CHECK_PAYMENT'
                }
              />
            ) : (
              <>
                {layoutType === 'PO_SYSTEM_APPROVAL' ||
                layoutType === 'PO_SYSTEM_APPROVAL_ACCOUNTING' ? (
                  <POItems
                    data={
                      approvalHistoryDetails?.approvalHistory
                        .approvalHistoryItemsByDisplayEntityApprovalId
                    }
                    onRefetch={() => onRefetchApprovalHistory?.()}
                    layoutType={layoutType}
                  />
                ) : layoutType === 'TA_SYSTEM_APPROVAL' ? (
                  <TripList
                    tripsData={
                      approvalHistoryDetails?.approvalHistory
                        .approvalHistoryItemsByDisplayEntityApprovalId
                    }
                    onRefetch={() => onRefetchApprovalHistory?.()}
                  />
                ) : (
                  <InvoiceAccountingList
                    data={
                      approvalHistoryDetails?.approvalHistory
                        .approvalHistoryItemsByDisplayEntityApprovalId
                    }
                  />
                )}
              </>
            )}
          </Stack>
        )}
      {approvalHistoryDetails?.approvalHistory
        ?.approvalSignaturesByEntityApprovalId.nodes &&
        approvalHistoryDetails.approvalHistory
          .approvalSignaturesByEntityApprovalId.nodes.length > 0 && (
          <Stack className={styles.tagsContainer} tokens={{ childrenGap: 20 }}>
            <Text variant="xLarge">Signatures</Text>
            <SignatureView
              signatureData={
                approvalHistoryDetails.approvalHistory
                  .approvalSignaturesByEntityApprovalId.nodes
              }
            />
          </Stack>
        )}
    </>
  );
};
